import moment from 'moment';

/**
 * Single source of truth for the Month view's rolling 8-week window math.
 *
 * The Month view is decoupled from calendar months: it always shows exactly
 * 8 weeks (56 days) starting at a Sunday "window start". Entry, the Today
 * button, and picker day-selection all center the window on a focus week
 * (2 weeks before the focus week becomes the top of the window); the header
 * label and the visible-date set derive from that same window start.
 */

export const MONTH_VIEW_WEEKS = 8;
export const MONTH_VIEW_WINDOW_DAYS = MONTH_VIEW_WEEKS * 7; // 56

/**
 * Sunday (YYYY-MM-DD) of the top week of the window when focusDate is the
 * focus week: 2 weeks before the focus week's start.
 */
export const getMonthViewWindowStart = (focusDate) => moment(focusDate)
  .startOf('week')
  .subtract(2, 'weeks')
  .format('YYYY-MM-DD');

/**
 * The 56 day-keys (YYYY-MM-DD) of the window, in order, starting at its top
 * Sunday.
 */
export const getMonthViewWindowDateKeys = (windowStart) => {
  const start = moment(windowStart, 'YYYY-MM-DD').startOf('week');
  const keys = [];
  for (let i = 0; i < MONTH_VIEW_WINDOW_DAYS; i += 1) {
    keys.push(start.clone().add(i, 'days').format('YYYY-MM-DD'));
  }
  return keys;
};

/**
 * Pick the calendar month (YYYY-MM) that occupies the most day-cells currently
 * visible in the viewport. `rows` are the window's week rows as
 * { weekStart: 'YYYY-MM-DD', top, height } measured against the scroll
 * container; a row counts when its [top, top+height] band intersects
 * [viewTop, viewBottom]. Ties keep the earliest (topmost) month. Returns null
 * when no row is visible (e.g. before layout is available), letting callers
 * fall back to a known reference.
 */
export const pickMostVisibleMonthKey = ({ rows, viewTop, viewBottom }) => {
  const counts = {};
  (rows || []).forEach(({ weekStart, top, height }) => {
    if (top + height <= viewTop || top >= viewBottom) return;
    for (let d = 0; d < 7; d += 1) {
      const key = moment(weekStart, 'YYYY-MM-DD').add(d, 'days').format('YYYY-MM');
      counts[key] = (counts[key] || 0) + 1;
    }
  });
  return Object.keys(counts).reduce(
    (best, key) => (counts[key] > (counts[best] || 0) ? key : best),
    null,
  );
};

/**
 * The window's "focused" month ('YYYY-MM') — the calendar month with the most
 * days inside the 8-week window (always the fully-contained month). Used to keep
 * that month full-brightness while the adjacent partial months are dimmed, so
 * the month in view reads as the focus regardless of scroll position.
 */
export const getDominantMonthKey = (windowStart) => {
  const counts = {};
  getMonthViewWindowDateKeys(windowStart).forEach((dayKey) => {
    const monthKey = dayKey.slice(0, 7);
    counts[monthKey] = (counts[monthKey] || 0) + 1;
  });
  return Object.keys(counts).reduce(
    (best, monthKey) => (counts[monthKey] > (counts[best] || 0) ? monthKey : best),
    null,
  );
};

/**
 * First day (a Date) of the month adjacent to monthKey ('YYYY-MM').
 * direction 'prev' → previous month; anything else → next month.
 */
export const getAdjacentMonthStart = (monthKey, direction) => moment(`${monthKey}-01`, 'YYYY-MM-DD')
  .add(direction === 'prev' ? -1 : 1, 'month')
  .startOf('month')
  .toDate();

/**
 * Combined month(s)/year(s) and the first→last week numbers spanned by the
 * window, for the calendar subheader (e.g. "Jun - Aug", "2026", W23–W30).
 */
export const getMonthViewWindowLabel = (windowStart) => {
  const start = moment(windowStart, 'YYYY-MM-DD').startOf('week');
  const end = start.clone().add(MONTH_VIEW_WINDOW_DAYS - 1, 'days');
  const monthsLabel = start.month() === end.month()
    ? start.format('MMM')
    : `${start.format('MMM')} - ${end.format('MMM')}`;
  const yearLabel = start.year() === end.year()
    ? start.format('YYYY')
    : `${start.format('YYYY')} - ${end.format('YYYY')}`;
  return {
    monthsLabel, yearLabel, firstWeek: start.week(), lastWeek: end.week(),
  };
};
